import { ChangeDetectorRef, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatToolbarModule } from '@angular/material/toolbar';
import { HttpClientModule } from '@angular/common/http';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { CustomSpinnerComponent } from '@core/components/ui-custom-spinner/custom-spinner.component';
import { NequiService } from '@core/services/nequi.service';
import type { PaymentDetails } from '@core/services/daviplata.interface';
import { ActivatedRoute, Router } from '@angular/router';
import type { DetailsModel } from '@core/components/ui-clinng-status-alert/ui-clinng-status-alert.component';
import type { TicketDetails } from '@core/components/ui-clinng-ticket-info/ui-clinng-ticket-info.component';
import { UiClinngButtonComponent } from '@core/components/ui-clinng-button/ui-clinng-button.component';
import { UiClinngDownloadButtonComponent } from '@core/components/ui-clinng-download-button/ui-clinng-download-button.component';
import { UiClinngErrorModalComponent } from '@core/components/ui-clinng-error-modal/ui-clinng-error-modal.component';
import { UiClinngStatusAlertComponent } from '@core/components/ui-clinng-status-alert/ui-clinng-status-alert.component';
import { UiClinngTicketInfoComponent } from '@core/components/ui-clinng-ticket-info/ui-clinng-ticket-info.component';
import { UiClinngPendingAlertComponent } from '@core/components/ui-clinng-pending-alert/ui-clinng-pending-alert.component';

import { CaptureScreenService } from '@core/services/capture-screen.service';
import { DownloadNequiVoucherComponent } from '../download-nequi-voucher/download-nequi-voucher.component';

import { CheckoutEstadoEnum } from '@core/models/states.enum';
import { ResponsiveService } from '@core/services/responsive.service';
import { ConnectionStatus, WebsocketService } from '@core/services/websocket.service';
import { finalize, Subscription, timeout } from 'rxjs';
import { deferViewUpdate } from '@core/utils/defer-view-update';
import { IconsEnum } from '@core/models/icons.enum';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import  moment from 'moment-timezone';


@Component({
  selector: 'checkout-nequi-form-container',
  standalone: true,
  imports: [CommonModule, FormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatButtonModule,
    MatInputModule,
    MatCheckboxModule,
    MatIconModule,
    MatToolbarModule,
    FormsModule,
    ReactiveFormsModule,
    HttpClientModule,
    MatSnackBarModule,
    MatProgressSpinnerModule,
    DownloadNequiVoucherComponent,
    CustomSpinnerComponent,
    UiClinngButtonComponent,
    UiClinngDownloadButtonComponent,
    UiClinngTicketInfoComponent,
    UiClinngStatusAlertComponent,
    MatDialogModule
  ],
  templateUrl: './checkout-nequi-form-container.component.html',
  styleUrl: './checkout-nequi-form-container.component.scss',
})
export class CheckoutNequiFormContainerComponent implements OnInit, OnDestroy {
  esResponsive$ = this.responsiveService.esResponsive$;
  estado: CheckoutEstadoEnum = CheckoutEstadoEnum.INGRESO_DATOS;
  estadoModel = CheckoutEstadoEnum;
  detailsModel!: DetailsModel;
  form!: FormGroup;
  transactionId!: string;
  verification!: string;
  aceptoTerminos: boolean = false;
  operationInfo!: PaymentDetails;
  ticketDetails: TicketDetails[] = [];
  download:boolean = false;
  success: boolean = false;
  intervalRef: any;
  timer!: string;
  screenRecordService = inject(CaptureScreenService)
  loading!: boolean;
  loadingButton: boolean = false;
  showContingencyButton = false;

  private readonly contingencyElapsedSeconds = 30;
  private timerInitialRemainingSeconds = 0;
  private websocketListenerId?: number;
  private pingInterval?: ReturnType<typeof setInterval>;
  private socketInitialized = false;
  private socketFlowFinished = false;
  private confirmingOperation = false;
  private readonly subscriptions = new Subscription();
  private readonly cdr = inject(ChangeDetectorRef);

  constructor(private nequiService: NequiService,
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private responsiveService: ResponsiveService,
    private matDialog: MatDialog,
    private websocketService: WebsocketService) {}

  ngOnInit(): void {
    this.loading = true;
    this.initForm();
    this.transactionId = this.route.snapshot.paramMap.get('transactionId') ?? '';
    this.verification = this.route.snapshot.queryParamMap.get('verification') ?? '';
    this.getPaymentDetails();
  }

  getPaymentDetails() {
    if (!this.transactionId || !this.verification) {
      deferViewUpdate(this.cdr, () => {
        this.loading = false;
        this.router.navigate(['/error/nequi']);
      });
      return;
    }

    this.nequiService.getPaymentDetails(this.transactionId, this.verification)
      .pipe(
        timeout(30_000),
        finalize(() => {
          deferViewUpdate(this.cdr, () => {
            this.loading = false;
          });
        }),
      )
      .subscribe({
        next: (data: any) => {
          deferViewUpdate(this.cdr, () => {
            this.operationInfo = data;
            this.setView();
          });
        },
        error: () => {
          deferViewUpdate(this.cdr, () => {
            this.router.navigate(['/error/nequi']);
          });
        },
      });
  }

  setView() {
    const processorName = this.operationInfo?.processor?.name ?? '';
    const status = String(this.operationInfo?.operation?.status ?? '');
    const invalid = processorName !== 'Nequi_wlt' || status !== '2';

    if (invalid) {
      this.router.navigate(['/error/nequi']);
      return;
    }

    this.ensureSocketTracking();

    if (this.operationInfo.operation.expirationDate && this.operationInfo.operation.hasSessionToken) {
      this.estado = CheckoutEstadoEnum.INGRESO_CODIGO;
      this.enterWaitingState();
      this.setTimer(this.operationInfo.operation.expirationDate);
    }
  }

  private enterWaitingState(): void {
    this.showContingencyButton = false;
    this.hydratePhoneNumberFromOperation();
  }

  private updateContingencyFromTimer(remainingSeconds: number): void {
    if (
      this.showContingencyButton ||
      this.estado !== CheckoutEstadoEnum.INGRESO_CODIGO ||
      this.socketFlowFinished
    ) {
      return;
    }

    const elapsedSeconds = this.timerInitialRemainingSeconds - remainingSeconds;
    if (elapsedSeconds >= this.contingencyElapsedSeconds) {
      this.showContingencyButton = true;
    }
  }

  private normalizePaymentStatus(status: string): 'approved' | 'denied' | 'pending' | null {
    if (status === '5' || status === '35') {
      return 'approved';
    }
    if (status === '6' || status === '34' || status === '71') {
      return 'denied';
    }
    if (status === '2' || status === '33') {
      return 'pending';
    }
    return null;
  }

  private buildViewDataFromSocket(
    socketData: Record<string, unknown>,
    isApproved: boolean,
    eventReceivedAt: Date,
  ) {
    return {
      statusCode: isApproved ? '5' : '6',
      success: isApproved,
      phoneNumber: this.resolvePhoneNumber({
        phoneNumber: socketData['phoneNumber'] != null ? String(socketData['phoneNumber']) : undefined,
        phone: socketData['phone'] != null ? String(socketData['phone']) : undefined,
      }),
      approvalNumber: String(
        socketData['approvalNumber'] ??
        socketData['messageId'] ??
        socketData['transactionId'] ??
        socketData['operationId'] ??
        socketData['idOperation'] ??
        this.operationInfo?.operation?.id ??
        '',
      ),
      operationDate: eventReceivedAt,
      errorDescription: socketData['errorDescription'] != null
        ? String(socketData['errorDescription'])
        : undefined,
    };
  }

  private navigateToFinalState(viewData: { statusCode: string; success: boolean }): void {
    this.showContingencyButton = false;
    this.matDialog.closeAll();
    this.loadingButton = false;
    this.success = viewData.success;
    this.setAlertDetails(viewData);
    this.setTicketDetails(viewData);
    this.estado = CheckoutEstadoEnum.FINALIZADO;
    if (this.intervalRef) {
      clearInterval(this.intervalRef);
    }
    this.finishSocketFlow();
  }

  private ensureSocketTracking(): void {
    const operationId = this.operationInfo?.operation?.id;
    if (operationId == null || this.socketInitialized || this.socketFlowFinished) {
      return;
    }

    this.websocketService.connect();
    this.socketInitialized = true;
    this.subscriptions.add(
      this.websocketService.getStatus().subscribe((connectionStatus) => {
        if (connectionStatus === ConnectionStatus.Connected && this.operationInfo?.operation?.id != null) {
          this.websocketService.sendMessage({
            topic: 'identifierVoucher',
            data: { idOperation: this.operationInfo.operation.id },
          });
        }
      })
    );

    this.websocketListenerId = this.websocketService.addMessageListener((message) => {
      if (message?.topic !== 'voucher-finished' || !message?.data) {
        return;
      }
      const incomingOpId = message.data.idOperation ?? message.data.operationId;
      const myId = this.operationInfo?.operation?.id;
      if (incomingOpId != null && myId != null && Number(incomingOpId) !== Number(myId)) {
        return;
      }
      this.applySocketPaymentStatus(message.data);
    });

    this.pingInterval = setInterval(() => {
      if (!this.socketFlowFinished) {
        this.websocketService.sendMessage({ topic: 'ping' });
      }
    }, 15000);
  }

  private applySocketPaymentStatus(socketData: Record<string, unknown>): void {
    const paymentStatus = this.normalizePaymentStatus(String(socketData['status'] ?? ''));
    if (this.socketFlowFinished || paymentStatus == null || paymentStatus === 'pending') {
      return;
    }

    if (
      this.estado === CheckoutEstadoEnum.INGRESO_DATOS ||
      this.estado === CheckoutEstadoEnum.FINALIZADO
    ) {
      return;
    }

    const isApproved = paymentStatus === 'approved';
    const eventReceivedAt = new Date();
    const viewData = this.buildViewDataFromSocket(socketData, isApproved, eventReceivedAt);

    deferViewUpdate(this.cdr, () => this.navigateToFinalState(viewData));
  }

  private applyConfirmOperationResult(data: any): void {
    deferViewUpdate(this.cdr, () => {
      const statusCode = String(data.statusCode ?? '');
      const isPending = statusCode === '2' || statusCode === '33';

      if (isPending) {
        this.setPendingDetails(data);
        return;
      }

      const viewData = {
        ...data,
        statusCode: statusCode || (data.success ? '5' : '6'),
        success: data.success === true || statusCode === '5' || statusCode === '35',
        phoneNumber: this.resolvePhoneNumber(data),
      };

      this.navigateToFinalState(viewData);
    });
  }

  private runConfirmOperation(): void {
    
    if (
      this.confirmingOperation ||
      this.estado === CheckoutEstadoEnum.FINALIZADO ||
      this.estado === CheckoutEstadoEnum.INGRESO_DATOS
    ) {
      return;
    }

    this.confirmingOperation = true;
    this.matDialog.closeAll();
    this.loadingButton = true;

    this.nequiService
      .confirmOperation(this.transactionId, this.form.value, this.verification)
      .pipe(
        timeout(30_000),
        finalize(() => {
          deferViewUpdate(this.cdr, () => {
            this.loadingButton = false;
            this.confirmingOperation = false;
          });
        }),
      )
      .subscribe({
        next: (data: any) => {
          this.applyConfirmOperationResult(data);
          const statusCode = String(data.statusCode ?? '');
          if (statusCode !== '2' && statusCode !== '33') {
            this.finishSocketFlow();
          }
        },
        error: () => {
          deferViewUpdate(this.cdr, () => {
            this.setErrorDetails();
            this.finishSocketFlow();
          });
        },
      });
  }

  private finishSocketFlow(): void {
    if (this.socketFlowFinished) {
      return;
    }
    this.socketFlowFinished = true;
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = undefined;
    }
    this.websocketService.closeConnection();
  }

  initForm(): void {
    this.form = this.fb.group({
      phoneNumber: ['', [Validators.required, Validators.maxLength(10), Validators.minLength(9)]],
    });
  }

  formatValue(key: string) {
    const formatDocument = this.form.controls[key].value.replace(/\D/g, '');
    this.form.controls[key].setValue(formatDocument);
  }
  phoneNumberRequiredValid(): boolean | undefined {
    return this.form.get('phoneNumber')?.hasError('required') && this.form.get('phoneNumber')?.touched
  }
  phoneNumberMinLengthValid(): boolean | undefined {
    return this.form.get('phoneNumber')?.hasError('minlength') && this.form.get('phoneNumber')?.touched
  }

  phoneNumberMaxLengthValid(): boolean | undefined {
    return this.form.get('phoneNumber')?.hasError('maxlength') && this.form.get('phoneNumber')?.touched
  }

  handleCheckboxClick(event: MouseEvent): void {
    event.stopPropagation();
  }

  downloadPdf(event: MouseEvent): void {
  
    event.stopPropagation();
    const link = document.createElement('a');
    link.href = 'assets/pdf/nequi/tc.pdf';
    link.download = 'T&C.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    event.stopPropagation();
    const link2 = document.createElement('a');
    link2.href = 'assets/pdf/nequi/politica.pdf';
    link2.download = 'Política de Privacidad de Datos.pdf';
    document.body.appendChild(link2);
    link2.click();
    document.body.removeChild(link2);
  }

  initOperation() {
    this.loadingButton = true;
    this.nequiService.initOperation(this.transactionId, this.form.value, this.verification).subscribe(
      {
        next: (data: any) => {
          this.success = data.success;
          if (data.success) {
            this.estado = CheckoutEstadoEnum.INGRESO_CODIGO;
            this.enterWaitingState();
            this.setTimer(data.expirationDate);
            this.ensureSocketTracking();
          } else {
            let message = data.errorMessage || data.errorDescription || 'Error interno';
            this.setErrorDetails(message);
          }
          this.loadingButton = false;
        },
        error: (error) => {
          this.loadingButton = false;
          this.setErrorDetails();
         
        },
        complete: () => {
          this.loadingButton = false;
        }
      })
  
  }

  confirmOperation(): void {
    this.runConfirmOperation();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    if (this.websocketListenerId !== undefined) {
      this.websocketService.removeMessageListener(this.websocketListenerId);
      this.websocketListenerId = undefined;
    }
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = undefined;
    }
    if (this.intervalRef) {
      clearInterval(this.intervalRef);
    }
    this.websocketService.closeConnection();
  }


  redirect(){
    if (this.estado === CheckoutEstadoEnum.FINALIZADO) {
      if (this.success) {
        window.location.href = this.operationInfo.operation.okUrl;
      } else {
        window.location.href = this.operationInfo.operation.errorUrl;
      }
    }
  }

 
  downloadVoucher(){
    this.download = true;
    setTimeout(() => {
     
      this.screenRecordService.captureWithPromise('comprobante-pago').then((imageData: string) => {
        this.download = false;
  
        const link = document.createElement('a');
        link.href = imageData;
        link.download = 'nequi-voucher.png';
        link.click();
      });
    }, 100);
  }



  setTimer(expirationDateResponse: string) {
    if (this.intervalRef) { clearInterval(this.intervalRef); }

    if(this.estado == CheckoutEstadoEnum.FINALIZADO || this.estado == CheckoutEstadoEnum.INGRESO_DATOS){
      return;
    }

    const finalexpirationDate = moment.utc(expirationDateResponse);
    this.timerInitialRemainingSeconds = Math.max(
      0,
      finalexpirationDate.diff(moment.utc(), 'seconds'),
    );

    const tick = () => {
      if (this.estado == CheckoutEstadoEnum.FINALIZADO || this.estado == CheckoutEstadoEnum.INGRESO_DATOS) {
        if (this.intervalRef) { clearInterval(this.intervalRef); }
        return;
      }

      const differenceInSeconds = finalexpirationDate.diff(moment.utc(), 'seconds');

      if (differenceInSeconds <= 0 || this.timer === '00 : 00') {
        clearInterval(this.intervalRef);
        this.expirateOperation();
        return;
      }

      this.timer = moment.utc(differenceInSeconds * 1000).format('mm : ss');
      this.updateContingencyFromTimer(differenceInSeconds);
    };

    deferViewUpdate(this.cdr, tick);
    this.intervalRef = setInterval(() => deferViewUpdate(this.cdr, tick), 1000);
  }


  expirateOperation() {
    this.finishSocketFlow();
    this.nequiService.expirateOperation(this.transactionId, this.operationInfo.verificationResult).subscribe(
      ({
        next: (data: any) => {
          clearInterval(this.intervalRef);
          const dialog = this.matDialog.open(UiClinngErrorModalComponent, {
            width: '503px',
            height: '428px',
            disableClose: true,
            data: {
              url: this.operationInfo?.operation?.errorUrl
            }
        });
        },
        error: (error) => {
          clearInterval(this.intervalRef);
          const dialog = this.matDialog.open(UiClinngErrorModalComponent, {
            width: '503px',
            height: '428px',
            disableClose: true,
            data: {
              url: this.operationInfo?.operation?.errorUrl
            }
        });

        }
      })
    )
  }

  setTicketDetails(data: any) {
    const phoneNumber = this.resolvePhoneNumber(data);
    const commonDetails = [
      {
        title: 'Número de línea',
        value: this.formatPhoneNumber(phoneNumber)
      },
      {
        title: 'Monto',
        value: `${this.operationInfo.operation.currencySymbol}${this.operationInfo.operation.amount}`
      },
      {
        title: 'Número de transacción',
        value: data.approvalNumber
      },
      {
        title: 'Fecha y hora',
        value: this.formatOperationDateColombia(data.operationDate),
      }
    ];
  
    if (data.success) {
      this.ticketDetails = commonDetails;
    } else {
      this.ticketDetails = [
        ...commonDetails,
        {
          title: 'Error',
          value: data.errorDescription ? data.errorDescription : 'Error interno'
        }
      ];
    }
  }

  setAlertDetails(data: any) {
    const statusCode = String(data.statusCode ?? '');
    const isApproved = statusCode === '5' || statusCode === '35' || data.success === true;
    this.detailsModel = {
      title: isApproved ? 'Transacción exitosa' : 'Transacción rechazada',
      mount: this.operationInfo.operation.currencySymbol + '' + this.operationInfo.operation.amount,
      img: isApproved ? IconsEnum.SUCCESS : IconsEnum.ERROR,
      color: isApproved ? 'blue' : 'red',
    };
  }


  setErrorDetails(message?: string) {
    if (this.intervalRef) {
      clearInterval(this.intervalRef);
    }
    this.showContingencyButton = false;
    this.setAlertDetails({success: false , statusCode: '6'});
    this.ticketDetails = [
      {
        title: 'Número de línea',
        value: this.formatPhoneNumber(this.resolvePhoneNumber())
      },
      {
        title: 'Monto',
        value: this.operationInfo.operation.currencySymbol + '' + this.operationInfo.operation.amount
      },
      {
        title: 'Error',
        value: message ? message : 'Error interno'
      },
      {
        title: 'Número de transacción',
        value: this.operationInfo.operation.id.toString()
      },
      {
        title: 'Fecha y hora',
        value: this.formatOperationDateColombia(),
      }
    ]
    this.estado = CheckoutEstadoEnum.FINALIZADO;
  }

  setPendingDetails(data: any) {
    this.loadingButton = false;
    const isMobile = window.innerWidth <= 480;
    const dialog = this.matDialog.open(UiClinngPendingAlertComponent, {
      width: isMobile ? '100vw' : '520px',
      height: isMobile ? '100vh' : '428px',
      maxWidth: isMobile ? '100vw' : '520px',
      maxHeight: isMobile ? '100vh' : '428px',
      disableClose: true,
      panelClass: isMobile ? 'pending-alert-mobile' : 'pending-alert-desktop',
      data: {
        onBack: () => {
          this.estado = CheckoutEstadoEnum.INGRESO_CODIGO;
          this.enterWaitingState();
          if (this.operationInfo.operation.expirationDate) {
            this.setTimer(this.operationInfo.operation.expirationDate);
          }
        }
      }
    });
  }

  private hydratePhoneNumberFromOperation(): void {
    const phoneNumber = this.resolvePhoneNumber();
    if (phoneNumber) {
      this.form.patchValue({ phoneNumber });
    }
  }

  private resolvePhoneNumber(data?: { phoneNumber?: string; phone?: string }): string {
    const fromData = data?.phoneNumber ?? data?.phone;
    if (fromData) {
      return fromData;
    }

    const fromForm = this.form?.value?.phoneNumber;
    if (fromForm) {
      return String(fromForm);
    }

    const attributeCodes = ['phoneNumber', 'phone_number', 'phone', 'mobile', 'cellphone'];
    for (const code of attributeCodes) {
      const value = this.operationInfo?.attributes?.find((attribute) => attribute.code === code)?.value;
      if (value) {
        return String(value);
      }
    }

    const customer = this.operationInfo?.customer as Record<string, unknown> | undefined;
    const fromCustomer = customer?.['phone'] ?? customer?.['phoneNumber'] ?? customer?.['mobile'];
    if (fromCustomer != null && fromCustomer !== '') {
      return String(fromCustomer);
    }

    return '';
  }

  private formatOperationDateColombia(date?: string | Date): string {
    const colombiaTime = date ? moment(date).tz('America/Bogota') : moment().tz('America/Bogota');
    if (!colombiaTime.isValid()) {
      return moment().tz('America/Bogota').format('DD/MM/YYYY - h:mm A');
    }
    return colombiaTime.format('DD/MM/YYYY - h:mm A');
  }

  formatPhoneNumber(phoneNumber?: string): string {
    if (!phoneNumber) {
      return '';
    }

    const cleanedValue = phoneNumber.replace(/\D+/g, '');
  
  
    let formattedValue = cleanedValue.replace(/(\d{3})(?=\d)/g, '$1-');
  
    return formattedValue;
  }
 
}
